import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { MAX_MANUAL_RECORD_REASON_LENGTH } from "../lib/manualRecord"

/**
 * The manual-record panel of the new-booking dialog (AB#868, `amsStaffManualRecords`): what is about
 * to happen, and the reason it needs. Same shape as `DailyLimitOverrideSection`; the caller owns the
 * button that submits with `manualRecordReason`.
 *
 * Two cases. `past`: the time has already passed, so the appointment is recorded in CMS alone, with
 * no booking in the scheduling system. `notice`: the scheduling system refused a time inside the
 * 8-hour notice, and it can be booked anyway. Either way the household is told nothing, which is the
 * thing staff most need to know before they click.
 */
export function ManualRecordSection({ id, kind, message, reason, onReasonChange, disabled }: {
  /** Unique within the dialog, for the textarea's label. */
  id: string
  kind: "past" | "notice"
  /** The server's refusal, for the notice case. The past case says its own. */
  message?: string
  reason: string
  onReasonChange: (reason: string) => void
  disabled?: boolean
}) {
  const past = kind === "past"
  return (
    <section
      data-testid="manual-record-section"
      className="rounded-md border border-amber-300 bg-amber-50 p-3 dark:bg-amber-950/30"
    >
      <p className="text-sm font-medium">
        {past ? "This time has already passed." : message || "The scheduling system refused this time."}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {past
          ? "The appointment will be recorded without scheduling: it is not added to the scheduling system, and the household will not be notified. Record its outcome afterwards as usual."
          : "You can book it anyway, inside the 8-hour notice. The household will not be notified about this appointment, so tell them yourself."}
        {" "}It is recorded against your name with the reason below.
      </p>
      <Label htmlFor={id} className="mt-3 block text-xs font-medium">
        {past ? "Reason for recording" : "Reason for booking without notifications"}{" "}
        <span aria-hidden className="text-destructive">*</span>
      </Label>
      <Textarea
        id={id}
        className="mt-1.5"
        rows={2}
        maxLength={MAX_MANUAL_RECORD_REASON_LENGTH}
        value={reason}
        disabled={disabled}
        required
        placeholder={past ? "e.g. Walk-in seen at the counter before it was booked" : "e.g. Walk-in with a shut-off notice, seen this afternoon"}
        onChange={event => onReasonChange(event.target.value)}
      />
    </section>
  )
}
